import React, { createContext, useContext, useState, ReactNode, useMemo } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getNotifications, getUnreadNotificationCount, markNotificationRead, markAllNotificationsRead } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";

interface NotificationContextType {
    isSidebarOpen: boolean;
    setIsSidebarOpen: (isOpen: boolean) => void;
    unreadCount: number;
    notifications: any[];
    isLoading: boolean;
    markAsRead: (id: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    refetch: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const { data: user } = useAuth();

    const getNotificationsArgs = useMemo(() => ({ limit: 20 }), []);
    const { data: notificationsData, isLoading: isLoadingNotifications, refetch: refetchNotifications } = useQuery(
        getNotifications,
        getNotificationsArgs,
        {
            enabled: !!user,
            refetchInterval: 300000, // Refetch every 5 minutes
            staleTime: 30000,        // Consider data fresh for 30 seconds
        }
    );

    const getUnreadArgs = useMemo(() => ({}), []);
    const { data: unreadData, refetch: refetchUnreadCount } = useQuery(
        getUnreadNotificationCount,
        getUnreadArgs,
        {
            enabled: !!user,
            refetchInterval: 300000, // Refetch every 5 minutes
            staleTime: 30000,        // Consider data fresh for 30 seconds
        }
    );

    const markReadAction = useAction(markNotificationRead);
    const markAllReadAction = useAction(markAllNotificationsRead);

    const markAsRead = async (id: string) => {
        await markReadAction({ notificationId: id });
        refetchNotifications();
        refetchUnreadCount();
    };

    const markAllAsRead = async () => {
        await markAllReadAction({});
        refetchNotifications();
        refetchUnreadCount();
    };

    const refetch = () => {
        refetchNotifications();
        refetchUnreadCount();
    };

    const value: NotificationContextType = {
        isSidebarOpen,
        setIsSidebarOpen,
        unreadCount: unreadData?.count || 0,
        notifications: notificationsData || [],
        isLoading: isLoadingNotifications,
        markAsRead,
        markAllAsRead,
        refetch,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotification() {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error("useNotification must be used within a NotificationProvider");
    }
    return context;
}
